import type { ReactNode, RefObject } from 'react';
import { TaskAudioPlayer, TaskItemCard, TaskItemField, TaskItemText } from '@/features/contribution-kit';
import type { ValidationAudioItem } from './types';
import { audioMimeType } from './validate-transcription.session';

type ValidateTranscriptionItemCardProps = {
  t: Record<string, unknown>;
  current: ValidationAudioItem;
  isPlaying: boolean;
  audioRef: RefObject<HTMLAudioElement>;
  canvasRef: RefObject<HTMLCanvasElement>;
  onPlayAudio: () => void;
  onAudioEnded: () => void;
  footer?: ReactNode;
};

/**
 * The audio whose text is checked: the French it should say (or the question
 * it answers) and the player. The proposed text is deliberately not repeated
 * here: it lives in the editor of the decision panel only.
 */
export function ValidateTranscriptionItemCard({
  t,
  current,
  isPlaying,
  audioRef,
  canvasRef,
  onPlayAudio,
  onAudioEnded,
  footer,
}: ValidateTranscriptionItemCardProps) {
  const s = (key: string) => String(t[key] ?? '');
  const frenchText = current.langFr || current.phraseFr;

  return (
    <div data-testid="validate-source-card">
      <TaskItemCard label={s('audioToValidate')} footer={footer}>
        {current.isInitialAnswer ? (
          // A free answer in the local language: its question is the text to match.
          <div className="space-y-1" data-testid="validate-answer-to-question">
            <p className="text-center text-xs font-medium text-muted-foreground">{s('answerToQuestion')}</p>
            <TaskItemText quoted={false} className="py-2 text-lg sm:text-xl">
              {frenchText || '-'}
            </TaskItemText>
            <p className="text-center text-xs leading-5 text-muted-foreground">{s('reviewAnswerHint')}</p>
          </div>
        ) : frenchText ? (
          <TaskItemField label={s('french')}>
            <span className="text-base font-medium">{frenchText}</span>
          </TaskItemField>
        ) : null}

        <TaskAudioPlayer
          isPlaying={isPlaying}
          onToggle={onPlayAudio}
          canvasRef={canvasRef}
          playLabel={s('playAudio')}
          pauseLabel={s('pauseAudio')}
        />
        <audio key={current.id} ref={audioRef} onEnded={onAudioEnded} className="hidden">
          <source src={current.audioUrl} type={audioMimeType(current.audioUrl)} />
        </audio>
      </TaskItemCard>
    </div>
  );
}
